/**
 * Hold the handheld one way up.
 *
 * `portrait` is not one orientation, it is two: it permits both
 * `portrait-primary` at 0 degrees and `portrait-secondary` at 180, excluding
 * only landscape. A device locked that way still turns itself upside down,
 * which is exactly what the MC220K was doing while the diagnostics reported
 * "lock: granted" and "orientation: portrait-secondary, angle 180".
 *
 * `portrait-primary` pins it to the single upright orientation.
 *
 * Locking needs a fullscreen or installed context. It is granted outright in
 * the home-screen app and refused in a plain browser tab, so a rejection is
 * an ordinary outcome rather than a fault.
 */
export const PORTRAIT = 'portrait-primary'

/** Request the upright lock. Resolves to whether it was granted. */
export async function lockPortrait() {
    try {
        if (typeof screen?.orientation?.lock !== 'function') return false
        await screen.orientation.lock(PORTRAIT)
        return true
    } catch {
        return false
    }
}

/** Release the lock. Safe to call when nothing was ever locked. */
export function unlockOrientation() {
    try {
        screen?.orientation?.unlock?.()
    } catch {
        // Never let releasing the lock break navigation away.
    }
}

/**
 * Keep the device upright for as long as the caller wants it.
 *
 * The plain lock is tried first, on its own. The browser refuses to lock
 * unless the page is fullscreen or installed, but an installed app satisfies
 * that on its own terms, so going fullscreen is a fallback for when the
 * browser actually says no rather than something to do up front. Nobody should
 * lose the status bar to a rule that did not apply to them.
 *
 * Beyond that, one attempt is not enough, for three reasons found on the
 * device:
 *
 *  - When fullscreen IS needed, the lock has to WAIT for it rather than fire
 *    alongside it. Doing both at once is why the automatic lock failed while
 *    the same two calls, awaited in order, succeeded from a button.
 *  - Fullscreen needs user activation, so an attempt made before anyone has
 *    touched the screen can fail through no fault of its own.
 *  - A lock can be dropped when the app is backgrounded, and the device can
 *    still turn before one is granted.
 *
 * So it tries at once, again on every interaction until it takes, again
 * whenever the device ends up off-square, and again on returning to the app.
 *
 * @param {() => boolean} [isEligible] Guards the fullscreen request, so this
 *   cannot throw a desktop browser into fullscreen just for being opened.
 * @returns {() => void} Stop, and release the lock.
 */
export function keepPortrait(isEligible = () => true) {
    let stopped = false
    let locked = false

    async function attempt() {
        if (stopped || locked) return

        // Ask plainly first. An installed app is usually allowed to lock
        // without fullscreen, and when it is, that is the whole job done.
        locked = await lockPortrait()
        if (locked || stopped) return

        // Refused. Fullscreen is the browser's precondition, so take it and
        // ask again — awaited, because the lock depends on it having landed.
        if (document.fullscreenElement || !isEligible()) return
        await requestFullscreen()
        if (stopped) return
        locked = await lockPortrait()
    }

    function retry() {
        locked = false
        attempt()
    }

    function onOrientationChange() {
        // Already upright: re-locking would only risk bouncing between events.
        if (screen?.orientation?.angle === 0) return
        retry()
    }

    function onVisibility() {
        if (!document.hidden) retry()
    }

    attempt()
    document.addEventListener('pointerdown', attempt)
    document.addEventListener('visibilitychange', onVisibility)
    screen?.orientation?.addEventListener?.('change', onOrientationChange)

    return function stop() {
        stopped = true
        document.removeEventListener('pointerdown', attempt)
        document.removeEventListener('visibilitychange', onVisibility)
        screen?.orientation?.removeEventListener?.('change', onOrientationChange)
        unlockOrientation()
    }
}

/** Fullscreen, awaited, because the orientation lock depends on it landing first. */
async function requestFullscreen() {
    try {
        await document.documentElement.requestFullscreen?.({ navigationUI: 'hide' })
        return true
    } catch {
        return false
    }
}

/**
 * How far to counter-rotate the page so it reads upright.
 *
 * A last resort that needs no permission at all. The MC220K reported
 * "lock: granted" while sitting at portrait-secondary, 180 degrees: the
 * browser accepted the lock and the window stayed flipped, most likely
 * because an installed app honours the orientation captured in its manifest
 * at install time, which was the looser "portrait" before it was corrected.
 *
 * Rather than argue with that, turn the page back. 180 needs no more than a
 * rotation, since the viewport keeps its shape. The quarter turns are left
 * alone: those would need the width and height swapped as well, and the lock
 * does reliably keep landscape out.
 *
 * @param {number|undefined} angle `screen.orientation.angle`
 * @returns {0|180} degrees to rotate the page by
 */
export function uprightRotation(angle) {
    return angle === 180 ? 180 : 0
}
